import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Collapse,
  IconButton,
  SelectChangeEvent,
  TablePagination,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Button,
  Search,
  Tag,
  ClearIcon,
  MultipleSelectFilterChip,
  KeyboardArrowDownIcon,
  Checkbox,
  KeyboardArrowRightIcon,
  EastIcon,
} from "@verily-src/react-design-system";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import SortableTableHeaderCell, {
  Order,
  orderByNumberAriaDescription,
  orderByTextAriaDescription,
} from "components/SortableTableHeaderCell";
import Loading from "components/Loading";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { format } from "date-fns";
import { AppRoutes } from "core/AppRoutes";
import { tablePaginationSxProps } from "components/Tables";
import pluralize from "pluralize";
import A11yStatusMessage from "components/A11yStatusMessage";

import { Download } from "@mui/icons-material";
import { focusOnRef, sxFocusWithin } from "common/Accessibility";
import { selectCurrentStudyRoles } from "core/UserConfigSlice";
import { Role } from "generated/studyauth/studyauth_pb";
import { ChangeLogRow } from "common/ChangeLogRow";
import { WhichChangeLogsTable } from "./ChangeLogsStateSlice";
import useChangeLogsDataLoader from "./ChangeLogsDataLoader";
import {
  FocusOn as StudyParticipantsFocusOn,
  setFocusTarget as studyParticipantsSetFocusTarget,
} from "studyparticipant/StudyParticipantsStateSlice";
import { FocusOn, setFocusTarget } from "common/CommonViewState";

interface ChangeLogsTableHeaderProps {
  order: Order;
  orderBy: string;
  onRequestSort: (orderBy: string) => void;
  whichChangeLogsTable: WhichChangeLogsTable;
  groupSimultaneousChanges: boolean;
  numCols: number;
}
const ChangeLogsTableHeader: React.FC<ChangeLogsTableHeaderProps> = ({
  order,
  orderBy,
  onRequestSort,
  whichChangeLogsTable,
  groupSimultaneousChanges,
  numCols,
}) => {
  const remainingWidth = groupSimultaneousChanges ? 75 : 85;

  return (
    <TableHead>
      <TableRow style={{ tableLayout: "fixed" }}>
        {groupSimultaneousChanges && (
          <TableCell sx={{ width: "10%" }}>
            <Typography variant="body2em">Expand Row</Typography>
          </TableCell>
        )}

        <SortableTableHeaderCell
          columnName="UpdatedTime"
          order={order}
          orderBy={orderBy}
          orderByAriaLabel="Date"
          orderByAriaDescription={orderByNumberAriaDescription}
          onRequestSort={onRequestSort}
          style={{ width: "15%" }}
        >
          <Typography variant="body2em">Date</Typography>
        </SortableTableHeaderCell>
        {(whichChangeLogsTable ===
          WhichChangeLogsTable.STUDY_PARTICIPANT_BY_PARTICIPANT_ID ||
          whichChangeLogsTable ===
            WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_STUDY_SITE_ID ||
          whichChangeLogsTable ===
            WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_REGISTRY_ID) && (
          <SortableTableHeaderCell
            columnName="Participant ID"
            order={order}
            orderBy={orderBy}
            orderByAriaLabel="Participant ID"
            orderByAriaDescription={orderByTextAriaDescription}
            onRequestSort={onRequestSort}
            sx={{ breakWord: "normal" }}
            style={{ width: remainingWidth / (numCols - 1) + "%" }}
          >
            <Typography variant="body2em">Participant&nbsp;ID</Typography>
          </SortableTableHeaderCell>
        )}
        {whichChangeLogsTable === WhichChangeLogsTable.STUDY_SITE && (
          <SortableTableHeaderCell
            columnName="Site Name"
            order={order}
            orderBy={orderBy}
            orderByAriaLabel="Site Name"
            orderByAriaDescription={orderByTextAriaDescription}
            onRequestSort={onRequestSort}
            style={{ width: remainingWidth / (numCols - 1) + "%" }}
          >
            <Typography variant="body2em">Site Name</Typography>
          </SortableTableHeaderCell>
        )}

        {(whichChangeLogsTable ===
          WhichChangeLogsTable.USERS_CDM_OR_SPONSOR_OPS_VIEW ||
          whichChangeLogsTable ===
            WhichChangeLogsTable.USERS_COMPLIANCE_ADMIN_VIEW) && (
          <SortableTableHeaderCell
            columnName="User Email"
            order={order}
            orderBy={orderBy}
            orderByAriaLabel="User Email"
            orderByAriaDescription={orderByTextAriaDescription}
            onRequestSort={onRequestSort}
            style={{ width: remainingWidth / (numCols - 1) + "%" }}
          >
            <Typography variant="body2em">User Email</Typography>
          </SortableTableHeaderCell>
        )}
        {!groupSimultaneousChanges && (
          <>
            <TableCell style={{ width: remainingWidth / (numCols - 1) + "%" }}>
              <Typography variant="body2em">Field</Typography>
            </TableCell>
            <TableCell style={{ width: remainingWidth / (numCols - 1) + "%" }}>
              <Typography variant="body2em">Old Value</Typography>
            </TableCell>
            <TableCell style={{ width: remainingWidth / (numCols - 1) + "%" }}>
              <Typography variant="body2em">New Value</Typography>
            </TableCell>
          </>
        )}
        <TableCell style={{ width: remainingWidth / (numCols - 1) + "%" }}>
          <Typography variant="body2em">Update Reason</Typography>
        </TableCell>
        <TableCell style={{ width: remainingWidth / (numCols - 1) + "%" }}>
          <Typography variant="body2em">User</Typography>
        </TableCell>
        {whichChangeLogsTable ===
          WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_REGISTRY_ID && (
          <TableCell style={{ width: remainingWidth / (numCols - 1) + "%" }}>
            <Typography variant="body2em">Site</Typography>
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  );
};

// Define a component to render change logs information.
interface ChangeLogRowProps {
  changeLogRow: ChangeLogRow;
  idToNameMap: Map<string, string>;
  whichChangeLogsTable: WhichChangeLogsTable;
  groupSimultaneousChanges: boolean;
  numCols: number;
}

const ChangeLogRowComponent: React.FC<ChangeLogRowProps> = ({
  changeLogRow,
  idToNameMap,
  whichChangeLogsTable,
  groupSimultaneousChanges,
  numCols,
}) => {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();

  useEffect(() => {
    if (!groupSimultaneousChanges) {
      setOpen(false);
    }
  }, [groupSimultaneousChanges]);

  const fieldsToBeRobotoMono = [
    "Participant ID",
    "Device ID",
    "Site Number",
    "Study ID",
  ];

  // These are the percentages that correspond to left padding, field, old value, arrow, new value, and right padding, respectively.
  // field, old value, arrow, and new value should add up to 100%.
  // If the left padding is 25, then the child table will start after the "Date" column.
  // If the left padding is 10, then the child table will start directly under the "Date" column.
  var childColsSpacing = [25, 31, 24, 8, 37, 15];

  if (numCols === 3) {
    childColsSpacing = [10, 20, 42, 8, 30, 15];
  }
  if (numCols === 4) {
    childColsSpacing = [10, 23, 30.5, 8, 38.5, 25];
  }

  return (
    <React.Fragment>
      <TableRow
        hover
        style={{
          tableLayout: "auto",
          width: "100%",
        }}
        key={
          changeLogRow.IdNumber + "" + changeLogRow.Version + changeLogRow.Field
        }
      >
        {groupSimultaneousChanges && (
          <TableCell
            style={{
              verticalAlign: "middle",
            }}
          >
            {changeLogRow.isFlagshipForSimultaneousChanges &&
              changeLogRow.SimultaneousFieldChangeLogs.length > 0 && (
                <IconButton
                  aria-label={`Display changes for ${
                    idToNameMap.get(changeLogRow.IdNumber) || ""
                  } Version ${changeLogRow.Version} 
                  ${open ? "expanded" : "collapsed"}`}
                  size="small"
                  aria-live="assertive"
                  onClick={() => setOpen(!open)}
                >
                  {open ? (
                    <KeyboardArrowDownIcon />
                  ) : (
                    <KeyboardArrowRightIcon />
                  )}
                </IconButton>
              )}
          </TableCell>
        )}
        <TableCell
          style={{
            verticalAlign: "middle",
            wordBreak: "break-word",
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: changeLogRow.UpdatedTime.toDate()
                .toLocaleString("en-US", {
                  year: "numeric",
                  month: "numeric",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })
                .replace(" PM", "&nbsp;PM")
                .replace(" AM", "&nbsp;AM"),
            }}
          />{" "}
        </TableCell>
        {whichChangeLogsTable !== WhichChangeLogsTable.COMPLIANCE_SETTINGS &&
          whichChangeLogsTable !== WhichChangeLogsTable.STUDY_DETAILS && (
            <TableCell
              style={{
                fontFamily:
                  whichChangeLogsTable === WhichChangeLogsTable.STUDY_SITE ||
                  whichChangeLogsTable ===
                    WhichChangeLogsTable.USERS_CDM_OR_SPONSOR_OPS_VIEW ||
                  whichChangeLogsTable ===
                    WhichChangeLogsTable.USERS_COMPLIANCE_ADMIN_VIEW
                    ? "Inter"
                    : "Roboto Mono",
                wordBreak: "break-word",
              }}
            >
              {idToNameMap.get(changeLogRow.IdNumber)}
            </TableCell>
          )}

        {!groupSimultaneousChanges && (
          <>
            <TableCell style={{ verticalAlign: "middle" }}>
              {changeLogRow.Field}
            </TableCell>
            <TableCell
              style={{
                verticalAlign: "middle",
                fontFamily: fieldsToBeRobotoMono.includes(changeLogRow.Field)
                  ? "Roboto Mono"
                  : "Inter",
                wordBreak: "break-word",
              }}
            >
              {changeLogRow.OldValue}
            </TableCell>
            <TableCell
              style={{
                verticalAlign: "middle",
                fontFamily: fieldsToBeRobotoMono.includes(changeLogRow.Field)
                  ? "Roboto Mono"
                  : "Inter",
                wordBreak: "break-word",
              }}
            >
              {changeLogRow.NewValue}
            </TableCell>
          </>
        )}
        <TableCell
          style={{
            verticalAlign: "middle",
            wordBreak: "break-word",
            textAlign: "left",
          }}
        >
          {changeLogRow.UpdateReason}
        </TableCell>
        <TableCell
          style={{
            verticalAlign: "middle",
            wordBreak: "break-word",
          }}
        >
          {changeLogRow.ActorEmail}
        </TableCell>
        {whichChangeLogsTable ===
          WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_REGISTRY_ID && (
          <TableCell
            style={{
              verticalAlign: "middle",
              wordBreak: "normal",
              overflowWrap: "normal",
            }}
          >
            {changeLogRow.Site}
          </TableCell>
        )}
      </TableRow>
      {groupSimultaneousChanges && (
        <TableRow>
          <TableCell
            style={{
              paddingBottom: 0,
              paddingTop: 0,
              tableLayout: "fixed",
              width: "100%",
              paddingLeft: "0px",
              paddingRight: "0px",
              alignContent: "center",
              justifyContent: "center",
            }}
            colSpan={10}
            aria-hidden={!open}
          >
            <Collapse
              in={open}
              timeout="auto"
              unmountOnExit
              style={{
                tableLayout: "fixed",
                width: "100%",
                paddingLeft: childColsSpacing[0] + "%",
                paddingRight: childColsSpacing[5] + "%",
                alignContent: "left",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "left",
                  alignContent: "left",
                  width: "100%",
                }}
              >
                <Table style={{ tableLayout: "fixed", width: "100%" }}>
                  <TableHead>
                    <TableRow style={{ tableLayout: "fixed" }}>
                      <TableCell>
                        <Typography
                          variant="body2em"
                          style={{ width: childColsSpacing[1] + "%" }}
                        >
                          Field
                        </Typography>
                      </TableCell>
                      <TableCell style={{ width: childColsSpacing[2] + "%" }}>
                        <Typography variant="body2em">Old Value</Typography>
                      </TableCell>
                      <TableCell
                        aria-hidden="true"
                        style={{ width: childColsSpacing[3] + "%" }}
                      />
                      <TableCell style={{ width: childColsSpacing[4] + "%" }}>
                        <Typography variant="body2em">New Value</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody
                    style={{
                      tableLayout: "fixed",
                      width: "100%",
                      paddingLeft: "0px",
                      paddingRight: "0px",
                    }}
                  >
                    {changeLogRow.SimultaneousFieldChangeLogs.map(
                      (fieldChangeLog) => (
                        <TableRow
                          key={
                            changeLogRow.IdNumber +
                            changeLogRow.Version +
                            fieldChangeLog.getField()
                          }
                          style={{
                            tableLayout: "fixed",
                            width: "100%",
                            paddingLeft: "0px",
                            paddingRight: "0px",
                          }}
                        >
                          <TableCell
                            align="left"
                            style={{ wordBreak: "break-word" }}
                          >
                            {fieldChangeLog.getField()}
                          </TableCell>
                          <TableCell
                            style={{
                              verticalAlign: "middle",
                              fontFamily: fieldsToBeRobotoMono.includes(
                                fieldChangeLog.getField()
                              )
                                ? "Roboto Mono"
                                : "Inter",

                              wordBreak: "break-word",
                            }}
                          >
                            {fieldChangeLog.getOldValue()}
                          </TableCell>
                          <TableCell
                            aria-hidden="true"
                            style={{
                              verticalAlign: "middle",
                              textAlign: "center",
                            }}
                          >
                            <EastIcon sx={{ color: "gray" }} />
                          </TableCell>
                          <TableCell
                            style={{
                              verticalAlign: "middle",
                              fontFamily: fieldsToBeRobotoMono.includes(
                                fieldChangeLog.getField()
                              )
                                ? "Roboto Mono"
                                : "Inter",
                              wordBreak: "break-word",
                              backgroundColor: (() => {
                                if (
                                  fieldChangeLog.getOldValue() !==
                                  fieldChangeLog.getNewValue()
                                ) {
                                  return theme.palette.primary.background?.toString();
                                } else {
                                  return "";
                                }
                              })(),
                            }}
                          >
                            {fieldChangeLog.getNewValue()}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};

interface ChangeLogsContentProps {
  changeLogRows: ChangeLogRow[];
  idToNameMap: Map<string, string>;
  whichChangeLogsTable: WhichChangeLogsTable;
  groupSimultaneousChanges: boolean;
  numCols: number;
}
const ChangeLogsContent: React.FC<ChangeLogsContentProps> = ({
  changeLogRows,
  idToNameMap,
  whichChangeLogsTable,
  groupSimultaneousChanges,
  numCols,
}) => {
  return (
    <TableBody>
      {changeLogRows.map((changeLogRow) => (
        <ChangeLogRowComponent
          key={
            changeLogRow.IdNumber + changeLogRow.Version + changeLogRow.Field
          }
          changeLogRow={changeLogRow}
          idToNameMap={idToNameMap}
          whichChangeLogsTable={whichChangeLogsTable}
          groupSimultaneousChanges={groupSimultaneousChanges}
          numCols={numCols}
        />
      ))}
    </TableBody>
  );
};

interface ChangeLogsTableControlProps {
  searchText: string;
  onRequestSearch(searchText: string): void;
  onFieldSelected(selectField: string[]): void;
  onUserSelected(selectUser: string[]): void;
  onSiteSelected(selectSite: string[]): void;
  selectField: string[];
  selectUser: string[];
  selectSite: string[];
  uniqueUsers: string[];
  uniqueSites: string[];
  whichChangeLogsTable: WhichChangeLogsTable;
  groupSimultaneousChanges: boolean;
  setGroupSimultaneousChanges(value: boolean): void;
  inTab: boolean;
}

const ChangeLogsTableControl: React.FC<ChangeLogsTableControlProps> = ({
  searchText,
  onRequestSearch,
  onFieldSelected,
  selectField,
  onUserSelected,
  selectUser,
  onSiteSelected,
  selectSite,
  uniqueUsers,
  uniqueSites,
  whichChangeLogsTable,
  groupSimultaneousChanges,
  setGroupSimultaneousChanges,
  inTab,
}) => {
  var FieldOptions = useMemo<Array<{ label: string; value: string }>>(() => {
    if (whichChangeLogsTable === WhichChangeLogsTable.COMPLIANCE_SETTINGS) {
      return [
        { label: "Wear Time Days Per Week", value: "Wear Time Days Per Week" },
        { label: "Wear Time Hours Per Day", value: "Wear Time Hours Per Day" },
        { label: "Sync Days Per Week", value: "Sync Days Per Week" },

        { label: "Meds Logged Per Day", value: "Meds Logged Per Day" },
        { label: "Meds Logged Per Week", value: "Meds Logged Per Week" },
        { label: "ECG Readings Per Day", value: "ECG Readings Per Day" },
      ];
    } else if (
      whichChangeLogsTable ===
        WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_STUDY_SITE_ID ||
      whichChangeLogsTable ===
        WhichChangeLogsTable.STUDY_PARTICIPANT_BY_PARTICIPANT_ID ||
      whichChangeLogsTable ===
        WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_REGISTRY_ID
    ) {
      return [
        { label: "Participant ID", value: "Participant ID" },
        { label: "Status", value: "Status" },
        { label: "Completed Date", value: "Completed Date" },
        { label: "Withdrawn Date", value: "Withdrawn Date" },
        { label: "Device ID", value: "Device ID" },
        { label: "Enrollment Date", value: "Enrollment Date" },
      ];
    } else if (whichChangeLogsTable === WhichChangeLogsTable.STUDY_SITE) {
      return [
        { label: "Site Name", value: "Site Name" },
        { label: "Site Number", value: "Site Number" },
        { label: "Principal Investigator", value: "Principal Investigator" },
        { label: "Country", value: "Country" },
        { label: "Status", value: "Status" },
        { label: "Archived Date", value: "Archived Date" },
      ];
    } else if (whichChangeLogsTable === WhichChangeLogsTable.STUDY_DETAILS) {
      return [
        { label: "Study Name", value: "Study Name" },
        { label: "Study ID", value: "Study ID" },
        { label: "Verily DM", value: "Verily DM" },
        { label: "Sponsor Name", value: "Sponsor Name" },
      ];
    } else if (
      whichChangeLogsTable ===
        WhichChangeLogsTable.USERS_CDM_OR_SPONSOR_OPS_VIEW ||
      whichChangeLogsTable === WhichChangeLogsTable.USERS_COMPLIANCE_ADMIN_VIEW
    ) {
      return [
        { label: "User Role", value: "User Role" },
        { label: "Invitation Status", value: "Invitation Status" },
        { label: "Account Type", value: "Account Type" },
        { label: "First Name", value: "First Name" },
        { label: "Last Name", value: "Last Name" },
        { label: "Email Address", value: "Email Address" },
      ];
    }
    return [];
  }, [whichChangeLogsTable]);

  const UserOptions = useMemo<Array<{ label: string; value: string }>>(
    () => [
      ...uniqueUsers.map((user) => {
        return { label: user, value: user };
      }),
    ],
    [uniqueUsers]
  );

  const SiteOptions = useMemo<Array<{ label: string; value: string }>>(
    () => [
      ...uniqueSites.map((site) => {
        return { label: site, value: site };
      }),
    ],
    [uniqueSites]
  );

  const handleFieldSelected = (event: SelectChangeEvent<unknown>) => {
    const {
      target: { value },
    } = event;
    onFieldSelected(
      (typeof value === "string" ? value.split(",") : value) as string[]
    );
  };

  const handleUserSelected = (event: SelectChangeEvent<unknown>) => {
    const {
      target: { value },
    } = event;
    onUserSelected(
      (typeof value === "string" ? value.split(",") : value) as string[]
    );
  };

  const handleSiteSelected = (event: SelectChangeEvent<unknown>) => {
    const {
      target: { value },
    } = event;
    onSiteSelected(
      (typeof value === "string" ? value.split(",") : value) as string[]
    );
  };

  const handleGroupSimultaneousChangesClicked = (
    event: React.SyntheticEvent<Element, Event>,
    checked: boolean
  ) => {
    setGroupSimultaneousChanges(checked);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "start",
        alignItems: "center",
        width: "100%",
        gap: "6px",
      }}
    >
      <Search
        placeHolder="Search"
        textFieldProps={{
          defaultValue: searchText,
          showClearInputButton: false,
          onChange: (e) => {
            onRequestSearch(e.target.value);
          },
        }}
        fullWidth={false}
        sx={{
          maxWidth:
            whichChangeLogsTable ===
            WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_REGISTRY_ID
              ? "155px"
              : inTab
              ? "200px"
              : undefined,
        }}
      />
      <MultipleSelectFilterChip
        placeholder="Field"
        value={selectField}
        onChange={handleFieldSelected}
        options={FieldOptions}
        role="listbox"
        aria-label={"Filter by field"}
        ariaLabel={"Filter by field"}
        sx={{
          width:
            whichChangeLogsTable ===
            WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_REGISTRY_ID
              ? "175px"
              : undefined,
        }}
        size="small"
      />
      <MultipleSelectFilterChip
        placeholder="User"
        value={selectUser}
        onChange={handleUserSelected}
        options={UserOptions}
        role="listbox"
        aria-label="Filter by user"
        ariaLabel={"Filter by user"}
        size="small"
      />
      {whichChangeLogsTable ===
        WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_REGISTRY_ID && (
        <MultipleSelectFilterChip
          placeholder="Site"
          value={selectSite}
          onChange={handleSiteSelected}
          options={SiteOptions}
          role="listbox"
          aria-label="Filter by site"
          ariaLabel={"Filter by site"}
          size="small"
          sx={{ width: "155px" }}
        />
      )}
      <Checkbox
        checked={groupSimultaneousChanges}
        onChange={handleGroupSimultaneousChangesClicked}
        label="Group changes"
        font-family="Inter"
      />
    </Box>
  );
};

const ChangeLogsTable: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const appState = useAppSelector((state) => state.appState);

  const previousPageContext = useAppSelector((state) => {
    return state.participantChangeLogState;
  });

  // Redirect to home screen when page is refreshed.
  useEffect(() => {
    if (!appState.userConfigured) {
      navigate(AppRoutes.ROOT);
    }
  }, [appState.userConfigured, navigate]);

  const [reloadData, setReloadData] = useState(true);
  const [lastReloadDate, setLastReloadDate] = useState(new Date());

  // Get StudySiteId
  const selectedRoles = useAppSelector((state) =>
    selectCurrentStudyRoles(state.userConfig)
  );

  // Note that CRCs can only manage one site per study.
  const currentStudySiteId = useMemo(() => {
    for (let userRole of selectedRoles) {
      if (userRole.role === Role.CRC) {
        return userRole.studySiteId;
      }
    }
    return "";
  }, [selectedRoles]);

  const {
    isLoading,
    hasError,
    changeLogRows,
    idToNameMap,
    uniqueUsers,
    uniqueSites,
    loadChangeLogRows,
  } = useChangeLogsDataLoader(
    currentStudySiteId,
    previousPageContext.participantId,
    previousPageContext.whichChangeLogsTable,
    previousPageContext.registryId
  );

  // Reload study participant change logs if the relevant information changes
  useEffect(() => {
    setReloadData(true);
  }, [currentStudySiteId, previousPageContext]);

  // Set last reload date when data is reloaded.
  useEffect(() => {
    if (reloadData) {
      loadChangeLogRows();
      setLastReloadDate(new Date());
      setReloadData(false);
    }
  }, [loadChangeLogRows, reloadData]);

  const [sortOrder, setSortOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState("UpdatedTime");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [searchText, setSearchText] = React.useState("");

  // Status message about the direction of sort for a11y support.
  const [sortDirectionMessage, setSortDirectionMessage] = useState("");

  const handleRequestSort = (orderByColumn: string) => {
    const isAsc = orderBy === orderByColumn && sortOrder === "asc";

    const newOrder = isAsc ? "desc" : "asc";
    setPage(0);
    setSortOrder(newOrder);
    setOrderBy(orderByColumn);
    setSortDirectionMessage(
      (isAsc ? "Sort descending on " : "Sort ascending on ") + orderByColumn
    );
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [filteredAndSortedChangeLogs, setFilteredAndSortedChangeLogs] =
    useState<ChangeLogRow[]>([]);

  // Stores previous search string.
  const previousSearch = useRef("");

  // Status message about number of search results for a11y support.
  const [searchResultMessage, setSearchResultMessage] = useState("");

  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [groupSimultaneousChanges, setGroupSimultaneousChanges] =
    useState(false);
  const dispatch = useAppDispatch();

  // Filter and sort study participant change logs.
  useEffect(() => {
    const records = filterAndSortChangeLogs(
      changeLogRows,
      searchText,
      orderBy,
      sortOrder,
      selectedFields,
      selectedUsers,
      selectedSites,
      idToNameMap,
      groupSimultaneousChanges
    );

    setFilteredAndSortedChangeLogs(records);

    // Only update status message if triggered by change in filter criteria
    // (i.e. search text)
    if (previousSearch.current !== searchText) {
      setSearchResultMessage(
        pluralize("search result", filteredAndSortedChangeLogs.length, true)
      );

      // Update previous states
      previousSearch.current = searchText;
    }
  }, [
    changeLogRows,
    searchText,
    orderBy,
    sortOrder,
    selectedFields,
    idToNameMap,
    selectedUsers,
    filteredAndSortedChangeLogs.length,
    selectedSites,
    groupSimultaneousChanges,
  ]);

  // export data and download as CSV
  function downloadCSV(
    data: ChangeLogRow[],
    idToNameMap: Map<string, string>,
    whichChangeLogsTable: WhichChangeLogsTable
  ) {
    var headers = [
      "Date",
      "Field",
      "Old Value",
      "New Value",
      "Update Reason",
      "User",
    ];
    var csvContent;
    var fileName = "change_logs.csv";
    if (whichChangeLogsTable === WhichChangeLogsTable.COMPLIANCE_SETTINGS) {
      fileName = "compliance_setting_change_logs.csv";
      csvContent =
        "data:text/csv;charset=utf-8," +
        headers.join(",") +
        "\n" +
        data
          .map((row) => {
            return [
              row.UpdatedTime.toDate().toLocaleString(),
              row.Field,
              row.OldValue,
              row.NewValue,
              row.UpdateReason,
              row.ActorEmail,
            ]
              .map((value) => `"${value}"`)
              .join(",");
          })
          .join("\n");
    } else if (
      whichChangeLogsTable ===
        WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_REGISTRY_ID ||
      whichChangeLogsTable ===
        WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_STUDY_SITE_ID ||
      whichChangeLogsTable ===
        WhichChangeLogsTable.STUDY_PARTICIPANT_BY_PARTICIPANT_ID
    ) {
      if (
        whichChangeLogsTable ===
        WhichChangeLogsTable.STUDY_PARTICIPANT_BY_PARTICIPANT_ID
      ) {
        fileName = "study_participants_change_logs.csv";
      } else {
        fileName = "study_participant_change_logs.csv";
      }
      headers.splice(1, 0, "Participant ID");
      headers.push("Site");
      csvContent =
        "data:text/csv;charset=utf-8," +
        headers.join(",") +
        "\n" +
        data
          .map((row) => {
            var sponsorParticipantId = idToNameMap.get(row.IdNumber);
            return [
              row.UpdatedTime.toDate().toLocaleString(),
              sponsorParticipantId,
              row.Field,
              row.OldValue,
              row.NewValue,
              row.UpdateReason,
              row.ActorEmail,
              row.Site,
            ]
              .map((value) => `"${value}"`)
              .join(",");
          })
          .join("\n");
    } else if (whichChangeLogsTable === WhichChangeLogsTable.STUDY_DETAILS) {
      fileName = "study_details_change_logs.csv";
      csvContent =
        "data:text/csv;charset=utf-8," +
        headers.join(",") +
        "\n" +
        data
          .map((row) => {
            return [
              row.UpdatedTime.toDate().toLocaleString(),
              row.Field,
              row.OldValue,
              row.NewValue,
              row.UpdateReason,
              row.ActorEmail,
            ]
              .map((value) => `"${value}"`)
              .join(",");
          })
          .join("\n");
    } else {
      if (whichChangeLogsTable === WhichChangeLogsTable.STUDY_SITE) {
        headers.splice(1, 0, "Site Name");
        fileName = "study_sites_change_logs.csv";
      } else if (
        whichChangeLogsTable ===
          WhichChangeLogsTable.USERS_CDM_OR_SPONSOR_OPS_VIEW ||
        whichChangeLogsTable ===
          WhichChangeLogsTable.USERS_COMPLIANCE_ADMIN_VIEW
      ) {
        headers.splice(1, 0, "User Email");
        fileName = "users_change_logs.csv";
      }
      csvContent =
        "data:text/csv;charset=utf-8," +
        headers.join(",") +
        "\n" +
        data
          .map((row) => {
            var nameOfResourceChanged = idToNameMap.get(row.IdNumber);
            return [
              row.UpdatedTime.toDate().toLocaleString(),
              nameOfResourceChanged,
              row.Field,
              row.OldValue,
              row.NewValue,
              row.UpdateReason,
              row.ActorEmail,
            ]
              .map((value) => `"${value}"`)
              .join(",");
          })
          .join("\n");
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  const Header = () => (
    <Box
      role="navigation"
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignContent: "center",
        padding: "24px 40px",
        background: theme.palette.background.canvas,
      }}
    >
      <Box
        sx={{
          display: "inherit",
          alignItems: "center",
        }}
      >
        <Box sx={{ marginRight: "12px", ...sxFocusWithin }}>
          <IconButton
            onClick={() => {
              if (previousPageContext.returnRoute) {
                dispatch(
                  studyParticipantsSetFocusTarget(
                    StudyParticipantsFocusOn.ChangeLogs
                  )
                );
                dispatch(setFocusTarget(FocusOn.ChangeLogs));
                navigate(previousPageContext.returnRoute);
              }
            }}
            aria-label="Exit"
          >
            <ClearIcon />
          </IconButton>
        </Box>

        <Typography
          ref={titleRef}
          tabIndex={-1}
          component="h1"
          variant="display5"
        >
          <span style={{ fontFamily: "Inter" }}> Data change logs </span>
          {previousPageContext.whichChangeLogsTable ===
            WhichChangeLogsTable.STUDY_PARTICIPANT_BY_PARTICIPANT_ID && (
            <>
              <span style={{ fontFamily: "Inter" }}>for Participant </span>
              <span style={{ fontFamily: "Roboto Mono" }}>
                {previousPageContext.sponsorParticipantId}
              </span>
            </>
          )}
        </Typography>
      </Box>
      <Button
        label="Export data"
        variant="filled"
        icon={<Download />}
        iconPosition="start"
        onClick={() => {
          downloadCSV(
            filterAndSortChangeLogs(
              changeLogRows,
              "",
              "UpdatedTime",
              "desc",
              [],
              [],
              [],
              idToNameMap,
              false
            ),
            idToNameMap,
            previousPageContext.whichChangeLogsTable
          );
        }}
        role="link"
        aria-label="Export data"
      />
    </Box>
  );

  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!isLoading) {
      focusOnRef(titleRef);
    } else {
      setTimeout(() => {
        focusOnRef(titleRef);
      }, 300);
    }
  }, [isLoading]);

  var numCols = 3;
  if (
    previousPageContext.whichChangeLogsTable === WhichChangeLogsTable.STUDY_SITE
  ) {
    numCols += 1;
  } else if (
    previousPageContext.whichChangeLogsTable ===
      WhichChangeLogsTable.USERS_CDM_OR_SPONSOR_OPS_VIEW ||
    previousPageContext.whichChangeLogsTable ===
      WhichChangeLogsTable.USERS_COMPLIANCE_ADMIN_VIEW
  ) {
    numCols += 1;
  } else if (
    previousPageContext.whichChangeLogsTable ===
    WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_REGISTRY_ID
  ) {
    numCols += 2;
  } else if (
    previousPageContext.whichChangeLogsTable ===
      WhichChangeLogsTable.STUDY_PARTICIPANT_BY_PARTICIPANT_ID ||
    previousPageContext.whichChangeLogsTable ===
      WhichChangeLogsTable.STUDY_PARTICIPANTS_BY_STUDY_SITE_ID
  ) {
    numCols += 1;
  }

  if (!groupSimultaneousChanges) {
    numCols += 3;
  }

  return (
    <div
      style={{
        overflow: !previousPageContext.inTab ? "auto" : "",
        maxHeight: !previousPageContext.inTab ? "100vh" : "",
        height: !previousPageContext.inTab ? "100vh" : "",
      }}
    >
      {isLoading && <Loading />}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          overflow: !previousPageContext.inTab ? "auto" : "hidden",
          overflowX: "hidden",
        }}
      >
        {!previousPageContext.inTab && <Header />}
        {hasError && !isLoading && (
          <Alert
            severity="error"
            sx={{ marginTop: "24px", paddingLeft: "16px" }}
          >
            Failed to load change logs.
          </Alert>
        )}
        {!isLoading && !hasError && (
          <>
            {!previousPageContext.inTab && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "start",
                  alignItems: "left",
                  justifyItems: "center",
                  padding: "24px 24px 24px 24px",
                }}
              >
                <Typography
                  component="h2"
                  variant="display6"
                  marginRight="24px"
                  marginLeft="12px"
                >
                  {filteredAndSortedChangeLogs.length} records
                </Typography>
                <Tag
                  color="primary"
                  label={`Last updated ${format(
                    lastReloadDate,
                    // date format example: 1/1/2024 3:30pm
                    "M/d/yyyy h:mmaaaaa'm'"
                  )}`}
                ></Tag>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                padding: previousPageContext.inTab
                  ? "6px 6px 0px 0px"
                  : "6px 24px 0px 24px",
              }}
            >
              <A11yStatusMessage message={searchResultMessage} />
              <A11yStatusMessage message={sortDirectionMessage} />
              <ChangeLogsTableControl
                searchText={searchText}
                onRequestSearch={(searchText) => {
                  setSearchText(searchText);
                  setPage(0);
                }}
                onFieldSelected={(selectedFields) => {
                  setSelectedFields(selectedFields);
                  setPage(0);
                }}
                selectField={selectedFields}
                onUserSelected={(selectedUsers) => {
                  setSelectedUsers(selectedUsers);
                  setPage(0);
                }}
                selectUser={selectedUsers}
                onSiteSelected={(selectedSites) => {
                  setSelectedSites(selectedSites);
                  setPage(0);
                }}
                selectSite={selectedSites}
                uniqueUsers={uniqueUsers}
                uniqueSites={uniqueSites}
                whichChangeLogsTable={previousPageContext.whichChangeLogsTable}
                groupSimultaneousChanges={groupSimultaneousChanges}
                setGroupSimultaneousChanges={setGroupSimultaneousChanges}
                inTab={previousPageContext.inTab ?? false}
              />
              {previousPageContext.inTab && (
                <Button
                  label="Export data"
                  variant="filled"
                  icon={<Download />}
                  iconPosition="start"
                  sx={{ width: "170px" }}
                  onClick={() => {
                    downloadCSV(
                      filteredAndSortedChangeLogs,
                      idToNameMap,
                      previousPageContext.whichChangeLogsTable
                    );
                  }}
                  role="link"
                  aria-label="Export data"
                />
              )}
            </Box>
            <Paper
              sx={{
                width: previousPageContext.inTab ? "100%" : "97%",
                marginTop: "16px",
                marginBottom: "32px",
                boxShadow: "none",
                marginRight: previousPageContext.inTab ? "0px" : "24px",
                marginLeft: previousPageContext.inTab ? "0px" : "24px",
                overflow: !previousPageContext.inTab ? "auto" : "",
                maxHeight: !previousPageContext.inTab ? "80vh" : "",
              }}
            >
              <TableContainer
                sx={{
                  maxHeight: !previousPageContext.inTab ? "92%" : "",
                  overflow: !previousPageContext.inTab ? "auto" : "",
                }}
              >
                <Table
                  style={{ tableLayout: "fixed" }}
                  stickyHeader
                  sx={{ minWidth: 700 }}
                  aria-label="Study participant change logs table"
                >
                  <ChangeLogsTableHeader
                    order={sortOrder}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                    whichChangeLogsTable={
                      previousPageContext.whichChangeLogsTable
                    }
                    groupSimultaneousChanges={groupSimultaneousChanges}
                    numCols={numCols}
                  />
                  {!hasError && (
                    <ChangeLogsContent
                      changeLogRows={filteredAndSortedChangeLogs.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )}
                      idToNameMap={idToNameMap}
                      whichChangeLogsTable={
                        previousPageContext.whichChangeLogsTable
                      }
                      groupSimultaneousChanges={groupSimultaneousChanges}
                      numCols={numCols}
                    />
                  )}
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredAndSortedChangeLogs.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ marginRight: "12px", ...tablePaginationSxProps }}
              />
            </Paper>
          </>
        )}
      </Box>
    </div>
  );
};

function filterAndSortChangeLogs(
  changeLogsRows: Array<ChangeLogRow>,
  searchText: string,
  orderBy: string,
  sortOrder: string,
  selectedFields: string[],
  selectedUsers: string[],
  selectedSites: string[],
  idToNameMap: Map<string, string>,
  groupSimultaneousChanges: boolean
) {
  return changeLogsRows
    .filter((changeLogsRow) => {
      var sponsorParticipantId = idToNameMap.get(changeLogsRow.IdNumber) || "";
      if (
        groupSimultaneousChanges &&
        !changeLogsRow.isFlagshipForSimultaneousChanges
      ) {
        return false;
      }
      if (
        !groupSimultaneousChanges &&
        changeLogsRow.OldValue === changeLogsRow.NewValue
      ) {
        return false;
      }
      if (selectedUsers.length > 0) {
        if (!selectedUsers.includes(changeLogsRow.ActorEmail)) {
          return false;
        }
      }
      if (selectedFields.length > 0) {
        if (
          !groupSimultaneousChanges &&
          !selectedFields.includes(changeLogsRow.Field)
        ) {
          return false;
        } else if (groupSimultaneousChanges) {
          let found = false;
          changeLogsRow.SimultaneousFieldChangeLogs.forEach(
            (fieldChangeLog) => {
              if (selectedFields.includes(fieldChangeLog.getField())) {
                found = true;
              }
            }
          );
          if (!found) {
            return false;
          }
        }
      }
      if (selectedSites.length > 0) {
        if (changeLogsRow.Site === undefined) {
          return false;
        }
        if (!selectedSites.includes(changeLogsRow.Site)) {
          return false;
        }
      }
      if (searchText !== "") {
        const searchTextLowercase = searchText.toLowerCase();
        if (
          !groupSimultaneousChanges &&
          sponsorParticipantId.toLowerCase().indexOf(searchTextLowercase) ===
            -1 &&
          changeLogsRow.ActorEmail.toLowerCase().indexOf(
            searchTextLowercase
          ) === -1 &&
          changeLogsRow.Field.toLowerCase().indexOf(searchTextLowercase) ===
            -1 &&
          changeLogsRow.NewValue.toLowerCase().indexOf(searchTextLowercase) ===
            -1 &&
          changeLogsRow.OldValue.toLowerCase().indexOf(searchTextLowercase) ===
            -1 &&
          changeLogsRow.UpdateReason.toLowerCase().indexOf(
            searchTextLowercase
          ) === -1 &&
          changeLogsRow.UpdatedTime.toDate()
            .toLocaleString()
            .toLowerCase()
            .indexOf(searchTextLowercase) === -1 &&
          changeLogsRow.Site !== undefined &&
          changeLogsRow.Site.toLowerCase().indexOf(searchTextLowercase) === -1
        ) {
          return false;
        } else if (
          groupSimultaneousChanges &&
          !changeLogsRow.SimultaneousFieldChangeLogs.some(
            (fieldChangeLog) =>
              fieldChangeLog
                .getField()
                .toLowerCase()
                .indexOf(searchTextLowercase) !== -1 ||
              fieldChangeLog
                .getOldValue()
                .toLowerCase()
                .indexOf(searchTextLowercase) !== -1 ||
              fieldChangeLog
                .getNewValue()
                .toLowerCase()
                .indexOf(searchTextLowercase) !== -1
          ) &&
          sponsorParticipantId.toLowerCase().indexOf(searchTextLowercase) ===
            -1 &&
          changeLogsRow.ActorEmail.toLowerCase().indexOf(
            searchTextLowercase
          ) === -1 &&
          changeLogsRow.UpdateReason.toLowerCase().indexOf(
            searchTextLowercase
          ) === -1 &&
          changeLogsRow.UpdatedTime.toDate()
            .toLocaleString()
            .toLowerCase()
            .indexOf(searchTextLowercase) === -1 &&
          changeLogsRow.Site !== undefined &&
          changeLogsRow.Site.toLowerCase().indexOf(searchTextLowercase) === -1
        ) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      let result = 0;
      switch (orderBy) {
        case "Version":
          result = a.Version - b.Version;
          break;
        case "UpdatedTime":
          const aTime = a.UpdatedTime.getSeconds() + a.UpdatedTime.getNanos();
          const bTime = b.UpdatedTime.getSeconds() + a.UpdatedTime.getNanos();
          result = aTime - bTime;
          break;

        case "Participant ID":
        case "Site Name":
        case "User Email":
          result = (idToNameMap.get(a.IdNumber) || "").localeCompare(
            idToNameMap.get(b.IdNumber) || ""
          );
          if (result === 0) {
            result =
              a.UpdatedTime.toDate().getTime() -
              b.UpdatedTime.toDate().getTime();
            if (sortOrder === "asc") {
              result = -result;
            }
          }
          break;
      }
      return sortOrder === "asc" ? result : -result;
    });
}

export default ChangeLogsTable;
